import type { LocationAddress, LocationCapability, LocationOperatingHours, LocationType, SupportedMode } from './locationOptions'
import { LOCATION_CAPABILITIES, LOCATION_TYPES, SUPPORTED_MODES, uniqueStringArray } from './locationOptions'
import { asNullableNumber } from './geo'

type TranslateLike = (key: string, fallback?: string) => string

type UnknownRecord = Record<string, unknown>

export type LocationRecord = {
  id: string
  tenantId: string | null
  organizationId: string | null
  name: string
  code: string | null
  type: LocationType | null
  unlocode: string | null
  address: LocationAddress
  supportedModes: SupportedMode[]
  capabilities: LocationCapability[]
  geofenceRadiusM: number | null
  slotBookingEnabled: boolean
  operatingHours: LocationOperatingHours | null
  avgHandlingTimeMin: number | null
  contactName: string | null
  contactPhone: string | null
  gateInstructions: string | null
  isActive: boolean
  latitude: number | null
  longitude: number | null
  createdAt: string | null
  updatedAt: string | null
}

type Option = { value: string; label: string }

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function readBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    if (value === 'true') return true
    if (value === 'false') return false
  }
  return fallback
}

function readArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return uniqueStringArray(value.map((entry) => (typeof entry === 'string' ? entry : String(entry ?? ''))))
}

function readDateIso(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString()
  }
  if (typeof value === 'string') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString()
  }
  return null
}

export function normalizeLocationAddress(value: unknown): LocationAddress {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      street: '',
      city: '',
      postalCode: '',
      country: '',
      region: null,
    }
  }

  const record = value as UnknownRecord

  return {
    street: readString(record.street) ?? '',
    city: readString(record.city) ?? '',
    postalCode: readString(record.postalCode ?? record.postal_code) ?? '',
    country: readString(record.country) ?? '',
    region: readString(record.region),
  }
}

export function formatLocationAddress(address: LocationAddress): string {
  return [
    address.street,
    [address.postalCode, address.city].filter(Boolean).join(' '),
    address.region ?? null,
    address.country,
  ]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(', ')
}

export function normalizeLocationRecord(record: UnknownRecord): LocationRecord {
  const type = readString(record.type) as LocationType | null
  const supportedModes = readArray(record.supportedModes ?? record.supported_modes) as SupportedMode[]
  const capabilities = readArray(record.capabilities) as LocationCapability[]

  return {
    id: readString(record.id) ?? '',
    tenantId: readString(record.tenantId ?? record.tenant_id),
    organizationId: readString(record.organizationId ?? record.organization_id),
    name: readString(record.name) ?? '',
    code: readString(record.code),
    type: type && LOCATION_TYPES.includes(type) ? type : null,
    unlocode: readString(record.unlocode),
    address: normalizeLocationAddress(record.address),
    supportedModes: supportedModes.filter((value): value is SupportedMode => SUPPORTED_MODES.includes(value)),
    capabilities: capabilities.filter((value): value is LocationCapability => LOCATION_CAPABILITIES.includes(value)),
    geofenceRadiusM: asNullableNumber(record.geofenceRadiusM ?? record.geofence_radius_m),
    slotBookingEnabled: readBoolean(record.slotBookingEnabled ?? record.slot_booking_enabled, false),
    operatingHours:
      record.operatingHours && typeof record.operatingHours === 'object' && !Array.isArray(record.operatingHours)
        ? (record.operatingHours as LocationOperatingHours)
        : record.operating_hours && typeof record.operating_hours === 'object' && !Array.isArray(record.operating_hours)
          ? (record.operating_hours as LocationOperatingHours)
          : null,
    avgHandlingTimeMin: asNullableNumber(record.avgHandlingTimeMin ?? record.avg_handling_time_min),
    contactName: readString(record.contactName ?? record.contact_name),
    contactPhone: readString(record.contactPhone ?? record.contact_phone),
    gateInstructions: readString(record.gateInstructions ?? record.gate_instructions),
    isActive: readBoolean(record.isActive ?? record.is_active, true),
    latitude: asNullableNumber(record.latitude),
    longitude: asNullableNumber(record.longitude),
    createdAt: readDateIso(record.createdAt ?? record.created_at),
    updatedAt: readDateIso(record.updatedAt ?? record.updated_at),
  }
}

function option(value: string, label: string): Option {
  return { value, label }
}

export function buildLocationTypeOptions(t: TranslateLike): Option[] {
  return [
    option('PORT', t('locations.values.types.port', 'Port')),
    option('RAIL_TERMINAL', t('locations.values.types.railTerminal', 'Rail terminal')),
    option('INTERMODAL', t('locations.values.types.intermodal', 'Intermodal')),
    option('WAREHOUSE', t('locations.values.types.warehouse', 'Warehouse')),
    option('DEPOT', t('locations.values.types.depot', 'Depot')),
    option('FACTORY', t('locations.values.types.factory', 'Factory')),
    option('CROSS_DOCK', t('locations.values.types.crossDock', 'Cross-dock')),
    option('YARD', t('locations.values.types.yard', 'Yard')),
  ]
}

export function buildSupportedModeOptions(t: TranslateLike): Option[] {
  return [
    option('ROAD', t('locations.values.modes.road', 'Road')),
    option('RAIL', t('locations.values.modes.rail', 'Rail')),
    option('SEA', t('locations.values.modes.sea', 'Sea')),
    option('INLAND_WATER', t('locations.values.modes.inlandWater', 'Inland water')),
  ]
}

export function buildLocationCapabilityOptions(t: TranslateLike): Option[] {
  return [
    option('CONTAINER', t('locations.values.capabilities.container', 'Container handling')),
    option('REEFER', t('locations.values.capabilities.reefer', 'Reefer support')),
    option('ADR', t('locations.values.capabilities.adr', 'ADR / hazardous goods')),
    option('CRANE', t('locations.values.capabilities.crane', 'Crane')),
    option('RAIL_SIDING', t('locations.values.capabilities.railSiding', 'Rail siding')),
    option('RAMP', t('locations.values.capabilities.ramp', 'Ramp')),
    option('COLD_STORAGE', t('locations.values.capabilities.coldStorage', 'Cold storage')),
    option('WEIGH_BRIDGE', t('locations.values.capabilities.weighBridge', 'Weigh bridge')),
    option('CUSTOMS', t('locations.values.capabilities.customs', 'Customs handling')),
  ]
}

export function getLocationTypeLabel(value: string | null | undefined, t: TranslateLike): string {
  return buildLocationTypeOptions(t).find((entry) => entry.value === value)?.label ?? (value ?? '—')
}

export function getSupportedModeLabels(values: string[] | null | undefined, t: TranslateLike): string[] {
  const labelMap = new Map(buildSupportedModeOptions(t).map((entry) => [entry.value, entry.label]))
  return (values ?? []).map((value) => labelMap.get(value) ?? value)
}

export function getLocationCapabilityLabels(values: string[] | null | undefined, t: TranslateLike): string[] {
  const labelMap = new Map(buildLocationCapabilityOptions(t).map((entry) => [entry.value, entry.label]))
  return (values ?? []).map((value) => labelMap.get(value) ?? value)
}
